import { z } from 'zod';

export const TransactionErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  externalResultCode: z.string(),
  internalCode: z.string().optional(),
  cause: z.array(z.unknown()).optional(),
});

// Payment Transaction DTO Schema
export const PaymentTransactionDtoSchema = z.object({
  id: z.string(),
  userId: z.string(),
  orderId: z.string(),
  type: z.string(),
  amount: z.number(),
  currency: z.string(),
  description: z.string().optional(),
  returnUrl: z.string().nullable(),
  redirectUrl: z.string().nullable(),
  paidAmount: z.number().nullable(),
  paidCurrency: z.string().nullable(),
  state: z.string(),
  error: TransactionErrorSchema.optional().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const CreatePaymentTransactionSchema = PaymentTransactionDtoSchema.pick({
  userId: true,
  orderId: true,
  amount: true,
  currency: true,
  description: true,
  returnUrl: true,
  redirectUrl: true,
});

export const CreateWithdrawPaymentTransactionSchema = PaymentTransactionDtoSchema.pick({
  userId: true,
  orderId: true,
  amount: true,
  currency: true,
  description: true,
  returnUrl: true,
});

export enum PaymentTransactionType {
  Deposit = 'deposit',
  Withdraw = 'withdraw',
}

export enum PaymentMethod {
  Card = 'card',
  DigitalCard = 'digital-card',
  Crypto = 'crypto',
  PrepaidCard = 'prepaid-card',
  CashApp = 'cashapp',
}

export const PAYTECH_PROVIDER_INTERNAL_CODES = {
  '1.01': 'COMMUNICATION_PROBLEM',
  '1.02': 'COMMUNICATION_PROBLEM',
  '1.03': 'INTERNAL_SERVER_ERROR',
  '1.04': 'TIMEOUT',
  '1.05': 'TERMINAL_NOT_FOUND',
  '1.07': 'ANTI_FRAUD_INTERNAL_DECLINED',
  '1.11': 'PROCESSING_LIMITS_REACHED',
  '3.00': 'ACQUIRER_DECLINED',
  '3.01': 'ANTI_FRAUD_ACQUIRER_DECLINED',
  '3.02': 'REQUEST_VALIDATION_ERROR',
  '3.03': 'PAYMENT_PROVIDER_ACQUIRER_MALFUNCTION',
  '3.05': 'ACQUIRER_LIMITS_REACHED',
  '3.06': 'CARD_DATA_INVALID',
  '3.07': 'CARD_DATA_INVALID',
  '3.08': 'TRANSACTION_LIMIT_EXCEEDED',
  '3.09': 'THREE_DS_INCOMPLETE',
  '3.10': 'UNKNOWN_PROVIDER_ERROR_CODE',
  '3.16': 'DUPLICATE_PAYMENT',
  '3.19': 'BLACKLIST_ERROR',
  '4.00': 'ISSUER_DECLINED',
  '4.01': 'INSUFFICIENT_FUNDS',
  '4.04': 'CARD_DATA_INVALID',
  '4.05': 'ISSUER_DECLINED',
  '4.09': 'ANTI_FRAUD_ISSUER_DECLINED',
  '5.00': 'THREE_DS_DECLINED',
  '5.01': 'THREE_DS_TIMEOUT',
  '6.02': 'BLACKLIST_ERROR',
  '6.14': 'BLACKLIST_ERROR',
  '6.38': 'WITHDRAWALS_IN_CURRENCY_LIMIT_EXCEEDED',
  '6.49': 'ACQUIRER_LIMITS_REACHED',
  '7.00': 'ANTI_FRAUD_EXTERNAL_DECLINED',
} as const;

export type PaytechProviderInternalCode
  = (typeof PAYTECH_PROVIDER_INTERNAL_CODES)[keyof typeof PAYTECH_PROVIDER_INTERNAL_CODES];

export const PaymentTransactionFailedMessageSchema = z.object({
  transactionId: z.string().nullish(),
  orderId: z.string(),
  paymentType: z.nativeEnum(PaymentTransactionType).nullish().catch(null),
  paymentMethod: z.nativeEnum(PaymentMethod).nullish().catch(null),
  paymentMethodType: z.nativeEnum(PaymentMethod).nullish().catch(null),
  internalCode: z.string().nullish(),
});

export type TransactionErrorDto = z.infer<typeof TransactionErrorSchema>;
export type PaymentTransactionDto = z.infer<typeof PaymentTransactionDtoSchema>;
export type CreatePaymentTransactionDto = z.infer<typeof CreatePaymentTransactionSchema>;
export type CreateWithdrawPaymentTransactionDto = z.infer<typeof CreateWithdrawPaymentTransactionSchema>;
export type PaymentTransactionFailedMessageDto = z.infer<typeof PaymentTransactionFailedMessageSchema>;
